import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { Box, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { useUrlReduxBidirectionalSync } from '../../hooks/useUrlReduxBidirectionalSync';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { TabPanel } from '../../sprouts/components/Tabs/TabPanel';
import { Tabs } from '../../sprouts/components/Tabs/Tabs';
import {
  selectCurrCustomerID,
  setCurrCustomerByID,
} from '../select/selectSlice';
import { BlockSettings } from './block/BlockSettings';
import { DeveloperSettings } from './developer/DeveloperSettings';
import { FeatureFlagSettings } from './featureFlags/FeatureFlagSettings';
import { GeneralSettings } from './general/GeneralSettings';
import { useSettingsPageParams } from './hooks/useSettingsPageParams';
import { VarietySettings } from './variety/VarietySettings';
// using enum here since MUI tabs are actually number indexed
enum SettingsTab {
  GENERAL,
  BLOCK,
  VARIETY,
  FEATURE_FLAGS,
  DEVELOPER,
}

export const SettingsPage = () => {
  const [tab, setTab] = useState<number>(SettingsTab.GENERAL);

  const { t } = useTranslation();

  // Handle URL parameters for Settings page (optional customerID for BlockSettings)
  const { params, updateParams } = useSettingsPageParams();

  // Bidirectional sync: URL ⟷ Redux for customerID
  useUrlReduxBidirectionalSync({
    urlValue: params.customerID,
    updateUrl: (newCustomerID: string) =>
      updateParams({ customerID: newCustomerID }),
    reduxSelector: selectCurrCustomerID,
    reduxAction: setCurrCustomerByID,
  });

  const handleCustomerChange = (newCustomerId: string) => {
    updateParams({ customerID: newCustomerId });
  };

  const { Reports, Developer, Diagnostics } = useFeatureFlags();

  const tabOptions = useMemo(() => {
    const options = [{ label: t('General'), tabId: SettingsTab.GENERAL }];

    if (Reports)
      options.push(
        { label: t('Block'), tabId: SettingsTab.BLOCK },
        { label: t('Variety'), tabId: SettingsTab.VARIETY }
      );

    if (Developer || Diagnostics) {
      options.push({
        label: t('Feature Flags'),
        tabId: SettingsTab.FEATURE_FLAGS,
      });
    }

    if (Developer) {
      options.push({
        label: t('Developer'),
        tabId: SettingsTab.DEVELOPER,
      });
    }

    return options;
  }, [Developer, Diagnostics, Reports, t]);

  return (
    <CanopyProvider>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          overflowY: 'scroll',
        }}
      >
        <Stack
          sx={{
            padding: 4,
            gap: 8,
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            width: '80%',
            minWidth: 'min-content',
            height: 'min-content',
          }}
        >
          <Tabs value={tab} onChange={setTab} options={tabOptions} />
          <Box sx={{ width: '100%' }}>
            <TabPanel tabId={SettingsTab.GENERAL} selectedTab={tab}>
              <GeneralSettings />
            </TabPanel>
            <TabPanel tabId={SettingsTab.BLOCK} selectedTab={tab}>
              <BlockSettings
                customerId={params.customerID}
                onCustomerChange={handleCustomerChange}
              />
            </TabPanel>
            <TabPanel tabId={SettingsTab.VARIETY} selectedTab={tab}>
              <VarietySettings />
            </TabPanel>
            <TabPanel tabId={SettingsTab.FEATURE_FLAGS} selectedTab={tab}>
              <FeatureFlagSettings />
            </TabPanel>
            <TabPanel tabId={SettingsTab.DEVELOPER} selectedTab={tab}>
              <DeveloperSettings />
            </TabPanel>
          </Box>
        </Stack>
      </Box>
    </CanopyProvider>
  );
};
